import { Children, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wallet, TrendingDown, TrendingUp, PieChart as PieIcon, Clock, MapPin, Activity, ExternalLink, Calculator, AlertTriangle, Info, Calendar, Settings2, Eye, EyeOff } from "lucide-react";
import InvoiceOriginStatus from "@/components/InvoiceOriginStatus";
import { formatIDR, fmtDate } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import BulkFillDueDateDialog from "@/components/finance-report/BulkFillDueDateDialog";
import CashFlowChart from "./CashFlowChart";
import PaymentDonut from "./PaymentDonut";
import ScheduleDrilldownDialog from "./ScheduleDrilldownDialog";
import ProfitStatusBadge from "@/components/cost-control/ProfitStatusBadge";
import ProfitDrilldownDialog from "@/components/cost-control/ProfitDrilldownDialog";
// 2026-06-20 (user request "REKONSILIASI PROGRESS PEMBAYARAN LINTAS HALAMAN"):
// SATU sumber angka pembayaran; tooltip WAJIB di setiap KPI.
import { breakdownFromSummary, PAYMENT_FORMULA_TOOLTIPS } from "@/lib/paymentBreakdown";
import { summarize, fmtWaktu, KIND_LABEL } from "@/lib/activityHumanize";

/**
 * RingkasanTab — tab Ringkasan Dashboard Monitoring (Fase 2, user request).
 * Menampilkan: 6 KPI, donut Progress Pembayaran, 2 line chart cash flow
 * (mingguan+bulanan), peta lokasi (iframe OpenStreetMap), waktu pelaksanaan,
 * aktivitas terbaru. Data dari GET /active-projects/{pid}/monitoring.
 */

/** Donut Progress Pembayaran diekstrak ke ./PaymentDonut (2026-06-20) — dipakai
 *  bersama RingkasanTab & CashFlowTab. */

/** Line chart cash flow — di ekstrak ke ./CashFlowChart (Fase 3 reuse). */

const KPI = ({ icon: Icon, label, value, tone = "navy", testid, sub, formula, onClick }) => {
  const toneCls = tone === "green" ? "text-emerald-700" : tone === "red" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-[#0B1D3A]";
  const interactive = typeof onClick === "function";
  return (
    <div
      className={`bg-white border border-slate-200 p-3 group ${interactive ? "cursor-pointer hover:bg-slate-50 transition-colors" : ""}`}
      data-testid={testid} title={formula}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
    >
      <div className="flex items-start gap-2">
        <Icon size={14} className={`${toneCls} shrink-0 mt-0.5`} />
        {/* R230 (2026-10-20, user request): label WRAP (bukan truncate) —
            teks judul kartu harus terbaca penuh, proporsional dgn kartu. */}
        <span className="overline text-[9px] text-slate-500 tracking-wider leading-snug break-words min-w-0 flex-1">{label}</span>
        {formula && <Info size={10} className="ml-auto text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" aria-label={`Rumus: ${formula}`} />}
      </div>
      <div className={`font-display text-lg tabular-nums mt-1 leading-tight break-words ${toneCls}`}>{value}</div>
      {sub && <div className="text-[9px] text-slate-500 tabular-nums mt-0.5">{sub}</div>}
    </div>
  );
};

/**
 * R230 (2026-10-20, user request): pengaturan visibilitas kartu KPI per user.
 * Setiap user ber-akses dapat menyembunyikan/menampilkan kartu mana saja;
 * preferensi tersimpan per akun (localStorage `ens_kpi_hidden_<userId>`).
 * Daftar kartu diturunkan dinamis dari props anak (testid + label) — kartu
 * baru otomatis muncul di pengaturan tanpa mengubah komponen ini.
 */
function KpiGridWithSettings({ children }) {
  const { user } = useAuth();
  const storageKey = `ens_kpi_hidden_${user?.id || "anon"}`;
  const [hidden, setHidden] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(storageKey) || "[]")); }
    catch { return new Set(); }
  });
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const kids = Children.toArray(children).filter((c) => c?.props?.testid);
  const visible = kids.filter((c) => !hidden.has(c.props.testid));
  const persist = (next) => { try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch { /* quota penuh — abaikan */ } };
  const toggle = (tid) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      persist(next);
      return next;
    });
  };
  const showAll = () => { const next = new Set(); setHidden(next); persist(next); };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-end relative" ref={popRef}>
        <button type="button" data-testid="kpi-settings-btn" onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 text-slate-600 hover:bg-slate-50 text-[10px] uppercase tracking-wider font-semibold"
          title="Atur kartu ringkasan yang ditampilkan — tersimpan per akun Anda">
          <Settings2 size={11} /> Atur Kartu{hidden.size > 0 ? ` (${hidden.size} disembunyikan)` : ""}
        </button>
        {open && (
          <div className="absolute right-0 top-7 z-30 bg-white border border-slate-300 shadow-lg w-64 max-h-80 overflow-y-auto" data-testid="kpi-settings-popover">
            <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Tampilkan Kartu</span>
              <button type="button" data-testid="kpi-settings-reset" onClick={showAll}
                className="text-[10px] text-[#0B1D3A] underline hover:text-[#12284d]">Tampilkan Semua</button>
            </div>
            {kids.map((c) => {
              const tid = c.props.testid;
              const shown = !hidden.has(tid);
              return (
                <label key={tid} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-[11px] text-slate-700"
                  data-testid={`kpi-toggle-${tid}`}>
                  <input type="checkbox" checked={shown} onChange={() => toggle(tid)} className="accent-[#0B1D3A]" />
                  {shown ? <Eye size={11} className="text-slate-400 shrink-0" /> : <EyeOff size={11} className="text-slate-300 shrink-0" />}
                  <span className="break-words leading-tight">{c.props.label || tid}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-2" data-testid="ringkasan-kpis">
        {visible}
      </div>
      {visible.length === 0 && (
        <div className="border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-[11px] text-slate-500" data-testid="kpi-all-hidden">
          Semua kartu disembunyikan — atur lewat tombol &quot;Atur Kartu&quot;.
        </div>
      )}
    </div>
  );
}

/** Donut Profit Cost Control — SVG native, konsisten dengan PaymentDonut.
 *  R227 (2026-10-20, user request "SELARASKAN PROFIT, MARGIN, DAN STATUS
 *  KELENGKAPAN BIAYA"): angka & label dari profit_view kanonis engine —
 *  "Selisih Sementara"/"Margin Sementara" + badge kelengkapan; margin null →
 *  "Margin belum dapat dihitung" (BUKAN 100%). Klik → drill-down rincian. */
function ProfitDonutCard({ ccSum, pid }) {
  const [drillOpen, setDrillOpen] = useState(false);
  const view = ccSum?.profit_view || null;
  const status = view?.completeness?.status || "unknown";
  const kontrak = Number(view?.revenue_basis?.value ?? ccSum?.total_kontrak ?? 0);
  const expense = Number(view?.cost_recognized ?? ccSum?.total_expense ?? 0);
  const selisih = Number(view?.selisih ?? ccSum?.profit ?? 0);
  const hasData = (ccSum?.item_count || 0) > 0;
  const margin = view ? view.margin : (kontrak > 0 ? (selisih / kontrak) * 100 : null);
  const selisihLabel = view?.labels?.selisih || "Selisih Sementara";
  // Warna arc: hijau HANYA untuk profit final (complete & positif); status
  // lain memakai amber/slate agar tidak terlihat "pasti menguntungkan".
  const arcProfit = status === "complete" && selisih >= 0 ? "#059669" : status === "incomplete" ? "#D97706" : "#94A3B8";
  const numCls = status === "complete" ? (selisih >= 0 ? "text-emerald-700" : "text-rose-700") : status === "incomplete" ? "text-amber-700" : "text-slate-600";
  const R = 52, C = 2 * Math.PI * R;
  const expPct = kontrak > 0 ? Math.min(100, (expense / kontrak) * 100) : 0;
  const profPct = kontrak > 0 && selisih > 0 ? Math.max(0, Math.min(100 - expPct, (selisih / kontrak) * 100)) : 0;
  const expDash = (expPct / 100) * C;
  const profDash = (profPct / 100) * C;
  return (
    <div className="bg-white border border-slate-200 p-4 flex flex-col" data-testid="profit-donut-section">
      <h3 className="text-sm font-semibold text-[#0B1D3A] mb-2 flex items-center gap-1.5 flex-wrap"><Calculator size={14} /> Selisih Kontrak–Biaya (Cost Control)
        {view && <ProfitStatusBadge view={view} small onClick={() => setDrillOpen(true)} testid="profit-donut-badge" />}
      </h3>
      {hasData ? (
        <button type="button" onClick={() => view && setDrillOpen(true)} data-testid="profit-donut"
          className={`flex items-center gap-4 flex-1 text-left ${view ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}`}
          title={view ? "Klik untuk rincian pembentuk angka" : undefined}>
          <svg width="140" height="140" viewBox="0 0 140 140" className="shrink-0">
            <circle cx="70" cy="70" r={R} fill="none" stroke="#E2E8F0" strokeWidth="14" />
            {expPct > 0 && (
              <circle cx="70" cy="70" r={R} fill="none" stroke="#E11D48" strokeWidth="14"
                strokeDasharray={`${expDash} ${C - expDash}`} strokeDashoffset={C / 4} transform="rotate(-90 70 70)" />
            )}
            {profPct > 0 && (
              <circle cx="70" cy="70" r={R} fill="none" stroke={arcProfit} strokeWidth="14" strokeLinecap="round"
                strokeDasharray={`${profDash} ${C - profDash}`} strokeDashoffset={C / 4 - expDash} transform="rotate(-90 70 70)" />
            )}
            <text x="70" y="76" textAnchor="middle" className={margin === null || status === "unknown" ? "fill-slate-400" : (margin >= 0 && status === "complete" ? "fill-emerald-700" : status === "incomplete" ? "fill-amber-700" : "fill-rose-700")} fontSize="22" fontWeight="700">{margin === null || status === "unknown" ? "—" : `${margin.toFixed(1)}%`}</text>
          </svg>
          <div className="text-xs space-y-1.5">
            {/* R229: status unknown → kartu TIDAK menampilkan angka selisih
                bertanda maupun persentase margin; hanya label + badge. */}
            <div className="flex items-center gap-2"><span className="w-2 h-2 inline-block" style={{ background: arcProfit }} /> {status === "unknown" ? "Status" : selisihLabel}
              {status === "unknown" ? (
                <span className="font-semibold text-[11px] italic text-slate-500 ml-1" data-testid="profit-donut-value">Belum Dapat Dipastikan</span>
              ) : (
                <span className={`font-semibold tabular-nums ml-1 ${numCls}`} data-testid="profit-donut-value">{formatIDR(selisih)}</span>
              )}</div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 bg-rose-600 inline-block" /> Biaya Diakui
              <span className="font-semibold tabular-nums text-rose-700 ml-1">{formatIDR(expense)}</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 bg-slate-300 inline-block" /> Dasar Pendapatan
              <span className="font-semibold tabular-nums text-slate-700 ml-1">{kontrak > 0 ? formatIDR(kontrak) : "—"}</span></div>
            {(view?.pending?.total ?? 0) > 0 && (
              <div className="flex items-center gap-2" data-testid="profit-donut-pending"><span className="w-2 h-2 bg-amber-500 inline-block" /> Biaya Tertunda
                <span className="font-semibold tabular-nums text-amber-700 ml-1">{formatIDR(view.pending.total)}</span></div>
            )}
            <div className="text-[10px] text-slate-500" data-testid="profit-donut-margin-note">
              {margin === null || status === "unknown" ? "Margin belum dapat dihitung" : `${view?.labels?.margin || "Margin Sementara"}: ${margin.toFixed(1)}%`}
            </div>
          </div>
        </button>
      ) : (
        <div className="flex-1 border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[11px] text-slate-400 text-center px-3 min-h-[120px]" data-testid="profit-donut-empty">
          Belum ada data Cost Control untuk project ini.
        </div>
      )}
      <Link to={`/projects/${pid}/cost-control`} data-testid="btn-open-cost-control"
        className="mt-3 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold">
        <Calculator size={11} /> {hasData ? "Buka Cost Control" : "Isi Tabel Cost Control"}
      </Link>
      <ProfitDrilldownDialog open={drillOpen} onOpenChange={setDrillOpen} view={view} projectName={null} />
    </div>
  );
}

export default function RingkasanTab({ project, mon, ccSum, identity, onDataChanged }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const canFillDueDate = !!user && canPerm(user, "finance_report", "fill_due_date");
  const [bulkFillOpen, setBulkFillOpen] = useState(false);
  const s = mon?.summary || {};
  // 2026-06-20 (user request "REKONSILIASI"): baca angka dari SATU sumber
  // (summary.payment_breakdown). Semua halaman lain (Keuangan, Cash Flow,
  // Laporan Keuangan, Export) memakai field yang sama.
  const bd = breakdownFromSummary(s);
  // NILAI KONTRAK = data induk (canonical). KOSONG → "Nilai Kontrak belum
  // diisi" — JANGAN fallback diam-diam ke Total Nilai WO dengan label yang
  // sama (aturan user).
  const cvParent = Number(s.parent_contract_value ?? project?.contract_value ?? 0);
  const subSum = Number(s.sub_value_sum || 0);
  const subLabel = project?.is_outlet ? "WO" : "VO";
  // 2026-06-21 (user request): Nilai Kontrak = JUMLAHAN induk + VO (umum) /
  // Σ WO (maintenance) — field total_value engine. Tanpa badge "beda" &
  // tanpa label "belum diisi" (aturan lama dihapus user).
  const totalCv = bd?.totalValue ?? (cvParent + subSum);
  // Angka yang WAJIB dipisahkan (spec 10 definisi):
  const inc = bd?.receivedTotal ?? Number(s.total_income || 0);
  const overdue = bd?.overdueReceivable ?? 0;
  const retentionNotYetDue = bd?.retentionNotYetDue ?? 0;
  const exp = Number(s.total_expense || 0);
  const cash = Number(s.cash_position || 0);
  const phys = Number(s.physical_progress_pct || 0);
  const wos = mon?.wo_list || [];
  const isWoProject = project?.project_type === "working_order";
  const isMaintenanceProject = !!project?.is_outlet && !isWoProject;
  const woTotal = wos.length;
  // 2026-06-20 (user request): definisi seragam — WO dibatalkan keluar dari
  // denominator; selesai = tahap Selesai Fisik/Invoice/Closed.
  const woCancelled = Number(s.wo_cancelled ?? wos.filter((w) => w.status === "cancelled" || w.status === "cancelled_by_owner").length);
  const woDone = Number(s.wo_done ?? wos.filter((w) => ["selesai_fisik", "invoice", "closed"].includes(w.stage) || w.status === "completed").length);
  const woEff = Math.max(0, woTotal - woCancelled);
  const maintenanceWorkPct = woEff > 0 ? (woDone / woEff) * 100 : 0;
  const workProgressValue = isWoProject
    ? (project?.status === "completed" ? "Sudah Selesai" : "Belum Selesai")
    : isMaintenanceProject
      ? `${maintenanceWorkPct.toFixed(0)}% · ${woDone}/${woEff} WO`
      : `${phys.toFixed(0)}%`;
  // SINKRONISASI SISA WAKTU PROYEK (2026-06-21, user request): seluruh angka
  // jadwal dari field kanonis `summary.schedule` backend (Asia/Jakarta).
  // Frontend TIDAK menghitung ulang selisih tanggal.
  const sched = s.schedule || {};
  const rem = sched.remaining_days ?? null;
  const overdueDays = sched.overdue_days || 0;
  const isLate = sched.schedule_status === "overdue";
  const completedLabel = sched.completed_label || null;

  // Waktu pelaksanaan — tanggal mulai/selesai bersumber dari data INDUK
  // (identity) bila tersedia; fallback ke field proyek sendiri.
  const startRaw = identity?.fields?.start_date || project?.vo_start_date || project?.start_date;
  const endRaw = identity?.fields?.end_date || project?.vo_end_date || project?.end_date;
  const durationDays = sched.total_duration_days ?? null;
  const elapsedDays = sched.elapsed_days ?? null;
  const elapsedPct = (durationDays && durationDays > 0 && elapsedDays !== null)
    ? Math.min(100, Math.round((elapsedDays / durationDays) * 100)) : 0;

  const loc = mon?.project?.location || {};
  // 2026-06-20 (user request): Google Plus Code jadi sumber utama peta.
  // Google Maps me-resolve plus code native — iframe embed pakai ?q=<code>&output=embed.
  const plusCode = (loc.plus_code || "").trim();
  const hasCoords = loc.latitude != null && loc.longitude != null;
  const mapSrc = plusCode
    ? `https://www.google.com/maps?q=${encodeURIComponent(plusCode)}&output=embed`
    : hasCoords
      ? `https://www.openstreetmap.org/export/embed.html?bbox=${loc.longitude - 0.02},${loc.latitude - 0.02},${loc.longitude + 0.02},${loc.latitude + 0.02}&layer=mapnik&marker=${loc.latitude},${loc.longitude}`
      : null;
  const openMapsHref = plusCode
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(plusCode)}`
    : hasCoords
      ? `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`
      : (loc.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address)}` : null);
  // 2026-06-21 (user request): pilihan grafik donut — satu per satu.
  const [donutView, setDonutView] = useState(() => {
    try { return localStorage.getItem("ens_donut_view") || "receipt"; } catch { return "receipt"; }
  });
  // 2026-10-21 (user request F): drill-down rincian jadwal via dialog read-only.
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  return (
    <div className="space-y-4" data-testid="ringkasan-tab">
      {/* KPI row — tiap kartu punya tooltip rumus (spec 2026-06-20).
          R230: label wrap (terbaca penuh) + visibilitas per kartu dapat diatur
          user via tombol "Atur Kartu" (tersimpan per akun). */}
      <KpiGridWithSettings>
        <KPI icon={Wallet} label="Nilai Kontrak"
          value={totalCv > 0 ? formatIDR(totalCv) : "—"}
          tone="navy" testid="kpi-nilai-kontrak"
          formula={PAYMENT_FORMULA_TOOLTIPS.contract_value}
          sub={subSum > 0 ? (
            <span data-testid="kpi-sub-value">
              {cvParent > 0 ? `Induk ${formatIDR(cvParent)} + ` : ""}{subLabel} {formatIDR(subSum)}
            </span>
          ) : null} />
        <KPI icon={TrendingUp} label="Uang Sudah Diterima" value={formatIDR(inc)} tone="green" testid="kpi-uang-masuk"
          formula={PAYMENT_FORMULA_TOOLTIPS.received} />
        {/* 2026-10-17 (user request): empat kartu piutang terpisah — SATU
            sumber dgn Dasbor & Collection (utils/ar_engine). Outstanding tidak
            disamakan dgn overdue; invoice tanpa due_date kategori terpisah. */}
        <KPI icon={Wallet} label="Total Piutang Terbuka"
          value={formatIDR(s.ar_breakdown?.total_outstanding ?? 0)}
          tone="navy" testid="kpi-ar-total"
          sub={<span className="text-slate-500">{(s.ar_breakdown?.total_count ?? 0)} transaksi · termasuk klaim retensi</span>}
          onClick={() => project?.id && nav(`/collection?project_id=${project.id}&status=outstanding_all`)}
          formula="Total Piutang Terbuka = invoice reguler terbuka (belum JT + lewat JT + tanpa due) + klaim retensi belum dibayar. Invoice lunas & draft TIDAK dihitung. Sumber: engine Rekonsiliasi A.R." />
        <KPI icon={Wallet} label="Belum Jatuh Tempo"
          value={formatIDR(s.ar_breakdown?.belum_jatuh_tempo?.total ?? 0)}
          tone="green" testid="kpi-ar-belum-jt"
          sub={<span className="text-slate-500">{s.ar_breakdown?.belum_jatuh_tempo?.count ?? 0} invoice{s.ar_breakdown?.belum_jatuh_tempo?.nearest_due ? ` · terdekat ${s.ar_breakdown.belum_jatuh_tempo.nearest_due}` : ""}</span>}
          onClick={() => project?.id && nav(`/collection?project_id=${project.id}&status=open`)}
          formula="Sisa piutang > 0, due_date tersedia, due_date ≥ hari ini." />
        <KPI icon={Wallet} label="Invoice Lewat Jatuh Tempo"
          value={formatIDR(s.ar_breakdown?.lewat_jatuh_tempo?.total ?? 0)}
          tone={(s.ar_breakdown?.lewat_jatuh_tempo?.count ?? 0) > 0 ? "red" : "navy"} testid="kpi-ar-lewat-jt"
          sub={<span className="text-slate-500">{s.ar_breakdown?.lewat_jatuh_tempo?.count ?? 0} invoice{(s.ar_breakdown?.lewat_jatuh_tempo?.max_days ?? 0) > 0 ? ` · maks ${s.ar_breakdown.lewat_jatuh_tempo.max_days} hari` : ""}</span>}
          onClick={() => project?.id && nav(`/collection?project_id=${project.id}&status=overdue`)}
          formula="Keterlambatan PEMBAYARAN (bukan jadwal proyek): sisa piutang > 0, due_date tersedia, due_date < hari ini." />
        <KPI icon={Wallet} label="Jatuh Tempo Belum Diisi"
          value={formatIDR(s.ar_breakdown?.no_due_date?.total ?? 0)}
          tone={(s.ar_breakdown?.no_due_date?.count ?? 0) > 0 ? "amber" : "navy"} testid="kpi-ar-no-due"
          sub={
            <span className="text-slate-500 flex items-center gap-1.5 flex-wrap">
              <span>{s.ar_breakdown?.no_due_date?.count ?? 0} invoice · perlu dilengkapi</span>
              {canFillDueDate && (s.ar_breakdown?.no_due_date?.count ?? 0) > 0 && (
                <button
                  type="button"
                  data-testid="btn-fill-due-date-monitoring"
                  onClick={(e) => { e.stopPropagation(); setBulkFillOpen(true); }}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[9px] font-semibold uppercase tracking-wider"
                >
                  <Calendar size={9} /> Isi Due Date
                </button>
              )}
            </span>
          }
          onClick={() => project?.id && nav(`/collection?project_id=${project.id}&status=no_due_date`)}
          formula="Sisa piutang > 0 tetapi due_date kosong — bukan overdue, perlu tindak lanjut FAT." />
        {/* 2026-10-18 (closeout #4): komponen klaim retensi ditampilkan
            terpisah — bagian Total Piutang Terbuka, bukan invoice reguler;
            aging memakai due date klaim sendiri. */}
        <KPI icon={Wallet} label="Klaim Retensi Menunggu Pembayaran"
          value={formatIDR(s.ar_breakdown?.klaim_retensi_belum_dibayar?.total ?? 0)}
          tone={(s.ar_breakdown?.klaim_retensi_belum_dibayar?.count ?? 0) > 0 ? "amber" : "navy"} testid="kpi-ar-klaim-retensi"
          sub={<span className="text-slate-500">{s.ar_breakdown?.klaim_retensi_belum_dibayar?.count ?? 0} klaim · komponen Total Piutang</span>}
          onClick={() => project?.id && nav(`/collection?project_id=${project.id}&status=retention_claim`)}
          formula="Klaim retensi belum dibayar penuh (menunggu pembayaran & dibayar sebagian) — komponen Total Piutang Terbuka, TIDAK masuk 3 kategori invoice reguler di atas." />
        {/* 2026-10-19 (user request anti double-counting): kartu Perlu
            Verifikasi Klaim Retensi — klaim tanpa tautan invoice sumber (legacy),
            tautan invalid, atau over-claim. FAT buka Collection untuk pilih
            invoice sumber via dialog Tautkan. */}
        <KPI icon={AlertTriangle} label="Klaim Retensi Perlu Verifikasi"
          value={formatIDR(s.ar_breakdown?.retention_claim_needs_verification?.remaining_total ?? 0)}
          tone={(s.ar_breakdown?.retention_claim_needs_verification?.count ?? 0) > 0 ? "amber" : "navy"} testid="kpi-ar-klaim-verif"
          sub={<span className="text-slate-500">{s.ar_breakdown?.retention_claim_needs_verification?.count ?? 0} klaim · sisa belum diterima · neto {formatIDR(s.ar_breakdown?.retention_claim_needs_verification?.net_total ?? 0)}</span>}
          onClick={() => nav(`/collection?tab=incomplete&sub=retention${project?.project_name ? `&project=${encodeURIComponent(project.project_name)}` : ""}`)}
          formula="Klaim retensi tanpa tautan invoice sumber (legacy), tautan invalid, atau nilai klaim > sisa retensi sumber. FAT buka Collection → tombol 'Perlu Verifikasi · Tautkan' pada baris klaim untuk pilih invoice sumber." />
        <KPI icon={TrendingDown} label="Uang Keluar" value={formatIDR(exp)} tone="red" testid="kpi-uang-keluar"
          formula="Uang Keluar = Σ expense_entries (biaya proyek + Cost Control)." />
        <KPI icon={Wallet} label="Posisi Kas" value={formatIDR(cash)} tone={cash < 0 ? "red" : "navy"} testid="kpi-posisi-kas"
          formula="Posisi Kas = Uang Sudah Diterima − Uang Keluar." />
        <KPI icon={PieIcon} label="Progress Pekerjaan" value={workProgressValue} tone="navy" testid="kpi-progress-fisik"
          formula="Progress Pekerjaan = WO Selesai ÷ WO Efektif (WO Dibatalkan dikeluarkan dari denominator)." />
        {/* 2026-10-18 (user request): istilah keterlambatan DIPISAH tegas —
            "Jadwal Proyek Terlambat" = waktu pelaksanaan proyek, BUKAN
            keterlambatan invoice (yang tampil di kartu Invoice Lewat Jatuh
            Tempo). */}
        <KPI icon={Clock} label="Sisa Waktu (Jadwal Proyek)"
          value={isLate ? `${overdueDays} hari` : rem == null ? "—" : `${rem} hari`}
          sub={isLate ? <span className="text-rose-700 font-semibold" data-testid="kpi-jadwal-terlambat">Jadwal Proyek Terlambat</span> : completedLabel ? <span className="text-emerald-700 font-semibold" data-testid="kpi-jadwal-selesai">{completedLabel}</span> : null}
          tone={isLate ? "red" : rem != null && rem <= 14 ? "amber" : "navy"} testid="kpi-sisa-waktu"
          onClick={() => setScheduleDialogOpen(true)}
          formula="Klik untuk rincian jadwal (asia_jakarta, rumus eksklusif end − start). Sumber: project_schedule_engine backend — frontend tidak menghitung ulang." />
      </KpiGridWithSettings>
      <ScheduleDrilldownDialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} sched={sched} />

      {(s.ar_breakdown?.pending_verification?.count ?? 0) > 0 && (
        <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex items-center gap-2 flex-wrap" data-testid="ar-pending-verification-note">
          <AlertTriangle size={12} className="flex-shrink-0" />
          <span>
            Draft bersisa (belum diverifikasi FAT): <b>{s.ar_breakdown.pending_verification.count} invoice · Sisa Draft {formatIDR(s.ar_breakdown.pending_verification.total)}</b> — bukan piutang resmi; tidak dihitung dalam kartu piutang di atas sampai diverifikasi FAT.
          </span>
          <button type="button" data-testid="ar-pending-verification-open"
            onClick={() => project?.id && nav(`/collection?project_id=${project.id}&status=needs_verification`)}
            className="underline font-semibold hover:text-amber-700">Lihat daftar</button>
        </div>
      )}

      {/* 2 cash flow charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <CashFlowChart title="Arus Kas Mingguan" data={mon?.cash_flow_weekly} storageKey="weekly" testid="cashflow-weekly" />
        <CashFlowChart title="Arus Kas Bulanan" data={mon?.cash_flow_monthly} storageKey="monthly" testid="cashflow-monthly" />
      </div>

      {/* Donut + Lokasi + Waktu Pelaksanaan + Profit Cost Control */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-3">
      {/* Donut Progress Pembayaran — 2026-06-20 (user request): DUA metrik
          terpisah dari satu fungsi bersama (payment_progress di summary):
          Penerimaan thd Nilai Proyek (dasar berlabel) + Pelunasan Invoice.
          Persentase & sisa tidak pernah beda denominator. */}
      <div className="bg-white border border-slate-200 p-4" data-testid="progress-pembayaran-section">
        <h3 className="text-sm font-semibold text-[#0B1D3A] mb-1">Progress Pembayaran</h3>
        <div className="border border-slate-200 bg-slate-50 px-3 py-2 mb-2" data-testid="mon-invoice-status-block">
          <InvoiceOriginStatus status={s.payment_breakdown?.invoice_status} variant="blocks" testid="mon-inv-status" />
        </div>
        {(() => {
          // 2026-06-21 (user request): donut ditampilkan SATU per satu via
          // tombol pilihan — dua donut berdampingan tumpang tindih dengan
          // teksnya. Preferensi tersimpan (localStorage). Angka tetap dari
          // payment_breakdown — satu sumber dgn KPI & tab Keuangan.
          // 2026-10-22 (user request "SINKRONISASI PROGRESS PEMBAYARAN RESMI"):
          // Donut "Penerimaan thd Nilai Proyek" sekarang memakai metrik resmi
          // kanonis (received_official / official_collectible_amount) agar
          // selaras dengan 4 blok Keuangan + Rekonsiliasi A.R. — tidak lagi
          // mencampur DP/retensi/legacy ke pembilang.
          const officialBase = bd?.officialCollectibleAmount ?? null;
          const officialRecv = bd?.receivedOfficialKlasifikasi ?? 0;
          const officialRemain = officialBase != null ? Math.max(0, officialBase - officialRecv) : null;
          const views = {
            receipt: {
              label: "Pembayaran Resmi",
              title: "Pembayaran Resmi — dasar: Kas Harus Diterima (invoice resmi terverifikasi)",
              donut: (
                <PaymentDonut
                  pct={bd?.officialPaymentProgress ?? null}
                  received={officialRecv}
                  remaining={officialRemain}
                  basisLabel="Kas Harus Diterima Resmi"
                  over={!!bd?.receiptOverOfficial}
                  testid="donut-receipt"
                />
              ),
            },
            settlement: {
              label: "Pelunasan Invoice",
              title: "Pelunasan Invoice — dasar: Kas Harus Diterima",
              donut: (
                <PaymentDonut
                  pct={bd?.invoiceSettlementPct ?? null}
                  received={bd?.receivedTotal ?? inc}
                  remaining={bd?.overdueReceivable ?? 0}
                  basisLabel="Kas Harus Diterima"
                  over={!!bd?.invoiceSettlementOver}
                  remainingLabel="Sisa Piutang Invoice"
                  testid="donut-invoice"
                />
              ),
            },
          };
          const active = views[donutView] || views.receipt;
          return (
            <div className="mt-2">
              <div className="flex items-center gap-1 mb-2" data-testid="donut-view-toggle">
                {Object.entries(views).map(([k, v]) => (
                  <button key={k} type="button"
                    onClick={() => { setDonutView(k); try { localStorage.setItem("ens_donut_view", k); } catch { /* storage penuh/private */ } }}
                    data-testid={`donut-toggle-${k}`}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold border transition-colors ${donutView === k ? "bg-[#0B1D3A] border-[#0B1D3A] text-white" : "border-slate-300 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-50"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2" data-testid="donut-active-title">{active.title}</div>
              {active.donut}
            </div>
          );
        })()}
      </div>
        <ProfitDonutCard ccSum={ccSum} pid={project?.id} />
        <div className="bg-white border border-slate-200 p-4" data-testid="lokasi-project-section">
          <h3 className="text-sm font-semibold text-[#0B1D3A] mb-2 flex items-center gap-1.5"><MapPin size={14} /> Lokasi Project</h3>
          {mapSrc ? (
            <iframe title="Peta Lokasi" src={mapSrc} className="w-full h-32 border border-slate-200" data-testid="lokasi-iframe" />
          ) : (
            <div className="h-32 border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[11px] text-slate-400 text-center px-3" data-testid="lokasi-empty">
              Belum ada Google Plus Code lokasi. Isi via tab Data Proyek.
            </div>
          )}
          <div className="text-[11px] text-slate-600 mt-2 space-y-0.5">
            {loc.address && <div className="font-medium text-[#0B1D3A]">{loc.address}</div>}
            {(loc.city || loc.province) && <div>{[loc.city, loc.province].filter(Boolean).join(", ")}</div>}
            {plusCode && <div className="text-slate-400 tabular-nums text-[10px]" data-testid="lokasi-plus-code">Plus Code: {plusCode}</div>}
            {!plusCode && hasCoords && <div className="text-slate-400 tabular-nums text-[10px]">{Number(loc.latitude).toFixed(4)}, {Number(loc.longitude).toFixed(4)} (koordinat lama)</div>}
          </div>
          {openMapsHref && (
            <a href={openMapsHref} target="_blank" rel="noreferrer" data-testid="btn-open-maps"
              className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold">
              <ExternalLink size={11} /> Buka di Peta
            </a>
          )}
        </div>
        <div className="bg-white border border-slate-200 p-4" data-testid="waktu-pelaksanaan-section">
          <h3 className="text-sm font-semibold text-[#0B1D3A] mb-3 flex items-center gap-1.5"><Clock size={14} /> Waktu Pelaksanaan</h3>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><div className="overline text-[9px] text-slate-500">Mulai</div><div className="tabular-nums font-semibold text-[#0B1D3A]">{startRaw ? fmtDate(startRaw) : "—"}</div></div>
            <div><div className="overline text-[9px] text-slate-500">Selesai</div><div className="tabular-nums font-semibold text-[#0B1D3A]">{endRaw ? fmtDate(endRaw) : "—"}</div></div>
            <div><div className="overline text-[9px] text-slate-500">Terpakai</div><div className="tabular-nums font-semibold text-sky-700" data-testid="jadwal-terpakai">{elapsedDays ?? "—"} hari</div></div>
            <div><div className="overline text-[9px] text-slate-500">Tersisa</div><div className={`tabular-nums font-semibold ${isLate ? "text-rose-700" : "text-[#0B1D3A]"}`} data-testid="jadwal-tersisa">{isLate ? `Jadwal Proyek Terlambat ${overdueDays} hari` : rem == null ? "—" : `${rem} hari`}</div></div>
          </div>
          {durationDays && (
            <div className="mt-3">
              <div className="h-2 bg-slate-100"><div className="h-full bg-sky-600" style={{ width: `${elapsedPct}%` }} /></div>
              <div className="text-[10px] text-slate-500 mt-1 tabular-nums text-right">{elapsedDays} hari dari {durationDays} hari · {elapsedPct}%</div>
            </div>
          )}
        </div>
      </div>

      {/* Aktivitas Terbaru */}
      <div className="bg-white border border-slate-200" data-testid="recent-activity-section">
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2">
          <Activity size={14} className="text-[#0B1D3A]" />
          <h3 className="text-sm font-semibold text-[#0B1D3A]">Aktivitas Terbaru</h3>
        </div>
        {(mon?.recent_activity?.length || 0) === 0 ? (
          <div className="p-4 text-[11px] text-slate-400 text-center" data-testid="activity-empty">Belum ada aktivitas terekam untuk project ini.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                {["Waktu", "Aktivitas", "Sumber", "Pengguna"].map((h) => (
                  <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mon.recent_activity.map((a) => (
                <tr key={a.id} className="border-b border-slate-100" data-testid={`recent-act-${a.id}`}>
                  <td className="px-3 py-1.5 tabular-nums text-slate-500 whitespace-nowrap">{fmtWaktu(a.at || a.created_at)}</td>
                  <td className="px-3 py-1.5 text-slate-700">{summarize(a).short}</td>
                  <td className="px-3 py-1.5 text-slate-500">{KIND_LABEL[a.kind] || KIND_LABEL[a.category] || "Project"}</td>
                  <td className="px-3 py-1.5 text-slate-700">{a.actor_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <BulkFillDueDateDialog
        open={bulkFillOpen}
        onClose={() => setBulkFillOpen(false)}
        projectId={project?.id}
        onSaved={() => { setBulkFillOpen(false); onDataChanged?.(); }}
      />
    </div>
  );
}
